import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Card, Pill } from '@/ui';
import pluralize from '@/utils/pluralize';

import type { Props } from './types';

const ScheduleCard = ({ schedule, onSelect }: Props) => {
  const hasPrograms = schedule.programs.length > 0;
  const metaLine = [
    pluralize(schedule.expectations.length, 'expectation'),
    pluralize(schedule.programs.length, 'program'),
    pluralize(schedule.patient_count, 'patient')
  ].join(' · ');

  return (
    <Card padding="none" className="overflow-hidden">
      <button
        type="button"
        onClick={onSelect}
        className="flex w-full flex-col border-0 bg-transparent p-0 text-left">
        <div className="flex flex-col gap-1.5 px-5 pb-3.5 pt-4">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-base font-semibold leading-tight text-uniform-gray-900 dark:text-uniform-gray-100">
              {schedule.name}
            </h2>
            <FontAwesomeIcon
              icon={faChevronRight}
              className="mt-0.5 shrink-0 text-xs text-uniform-gray-300 dark:text-uniform-gray-600"
            />
          </div>
          <div className="text-xs text-uniform-gray-500 dark:text-uniform-gray-400">{metaLine}</div>
        </div>

        <div className="border-t border-uniform-gray-100 dark:border-uniform-gray-700">
          {schedule.expectations.length === 0 ? (
            <p className="px-5 py-3 text-sm text-uniform-gray-400 dark:text-uniform-gray-500">
              No current expectations
            </p>
          ) : (
            schedule.expectations.map((expectation) => (
              <div
                key={expectation.appointment_type_id}
                className="flex items-baseline justify-between gap-3 border-b border-uniform-gray-100 dark:border-uniform-gray-700 px-5 py-3 last:border-b-0">
                <span className="text-sm font-medium text-uniform-gray-800 dark:text-uniform-gray-200">
                  {expectation.appointment_type_name}
                </span>
                <Pill
                  size="sm"
                  variant="subtle"
                  color="neutral"
                  className="shrink-0 whitespace-nowrap">
                  {expectation.badge_description}
                </Pill>
              </div>
            ))
          )}
        </div>

        <div className="flex flex-col gap-2 bg-uniform-gray-50 dark:bg-uniform-gray-800 px-5 pb-4 pt-3.5">
          <span className="text-[0.68rem] uppercase tracking-wide text-uniform-gray-400 dark:text-uniform-gray-500">
            {hasPrograms ? 'Assigned programs' : 'Programs'}
          </span>
          {hasPrograms ? (
            <div className="flex flex-wrap gap-1.5">
              {schedule.programs.map((program) => (
                <Pill key={program.id} size="sm" variant="outline" color="neutral">
                  {program.name}
                </Pill>
              ))}
            </div>
          ) : (
            <span className="text-sm text-uniform-gray-400 dark:text-uniform-gray-500">
              Not assigned to any program
            </span>
          )}
        </div>
      </button>
    </Card>
  );
};

export default ScheduleCard;
